import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Link } from 'react-router-dom';
import { CheckCircle2, PenLine, Timer } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ClaimDrawer } from './ClaimDrawer';

/**
 * Cola Intake → "To Sign": los contratos que el homeowner YA firmó y esperan la firma
 * del PA — el recordatorio de "me falta firmar yo". El más viejo primero; el botón
 * lleva directo al claim (tab Intake) donde vive el botón verde "Sign as PA".
 */

type Row = {
  contract_id: string; claim_id: string; homeowner_name: string | null; claim_number: string | null;
  state: string | null; pa_name: string | null; homeowner_signed_at: string; sent_to: string | null;
};

const daysSince = (iso: string): number => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

export function ToSignSection() {
  const { t } = useI18n();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.get<{ rows: Row[] }>('/api/contracts/to-sign')
      .then((r) => { if (alive) setRows(r.rows); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="page-empty">{t('common.loadingDots')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h2 className="card-title" style={{ margin: 0 }}><PenLine size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tosign.title')}</h2>
        <span className={`badge ${rows.length ? 'amber' : 'green'}`}>{rows.length}</span>
      </div>
      <p className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: -6, marginBottom: 14 }}>{t('tosign.hint')}</p>
      {rows.length === 0 ? (
        <div className="page-empty"><CheckCircle2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tosign.empty')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const days = daysSince(r.homeowner_signed_at);
            return (
              <div key={r.contract_id} className="card" style={{ margin: 0, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button
                  style={{ fontWeight: 'var(--fw-semibold)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'var(--fs-sm)', color: 'var(--accent)' }}
                  onClick={() => setSel(r.claim_id)}
                >
                  {r.homeowner_name ?? '—'}
                </button>
                {r.claim_number && <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }}>#{r.claim_number}</span>}
                {r.state && <span className="badge gray">{r.state}</span>}
                {r.pa_name && <span className="badge teal">PA: {r.pa_name}</span>}
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                  {t('tosign.signedOn', { when: new Date(r.homeowner_signed_at).toLocaleDateString(localeUI()) })}
                </span>
                <span style={{ flex: 1 }} />
                <span className={`badge ${days >= 2 ? 'red' : 'amber'}`}><Timer size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tosign.waiting', { n: String(days) })}</span>
                <Link to={`/claims/${r.claim_id}?tab=intake`} className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }}>
                  <PenLine size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tosign.goSign')}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      {sel && <ClaimDrawer claimId={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
